import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { StickFigureContainer } from './styles.js';
import uniqueId from 'lodash.uniqueid';
import { getConfettiColors, confettiColorSchemes } from './confettiColors';

const StickFigure = ({
  wrongGuesses,
  gameStatus = 'playing',
  selectedColorScheme = 'default',
}) => {
  const [fallingConfetti, setFallingConfetti] = useState([]);
  const [landedConfetti, setLandedConfetti] = useState([]);

  // Calculate which parts to show based on wrong guesses
  const showHead = wrongGuesses >= 1;
  const showBody = wrongGuesses >= 2;
  const showLeftArm = wrongGuesses >= 3;
  const showRightArm = wrongGuesses >= 4;
  const showLeftLeg = wrongGuesses >= 5;
  const showRightLeg = wrongGuesses >= 6;

  // Get confetti colors based on selected scheme or current date/holiday
  const getColors = useCallback(() => {
    return confettiColorSchemes[selectedColorScheme] || getConfettiColors();
  }, [selectedColorScheme]);

  // Generate a new confetti piece
  const generateConfettiPiece = useCallback(() => {
    const colors = getColors();

    // Generate random x position first
    const x = Math.random() * 160 + 20; // Random x position across the width

    // For Pride theme, use x position to determine color for rainbow effect
    let colorIndex;
    if (selectedColorScheme === 'pride') {
      // Map x position (20-180) to color index (0-5) for rainbow left to right
      const normalizedX = (x - 20) / 160; // 0 to 1
      colorIndex = Math.floor(normalizedX * colors.length);
      colorIndex = Math.min(colorIndex, colors.length - 1); // Ensure we don't go out of bounds
    } else {
      // Random color for other themes
      colorIndex = Math.floor(Math.random() * colors.length);
    }

    return {
      id: uniqueId('confetti-'),
      x: x,
      y: -10, // Start above the visible area
      color: colors[colorIndex],
      size: Math.random() * 3 + 2, // Back to original size 2-5
      speed: Math.random() * 2 + 1, // Random fall speed
      sway: Math.random() * 0.5 + 0.5, // Random sway amount
      swaySpeed: Math.random() * 0.02 + 0.01, // Random sway speed
      landed: false, // Track if piece has landed
      finalXOffset: Math.random() * 6 - 3, // Much tighter horizontal packing
    };
  }, [selectedColorScheme, getColors]);

  // Add confetti when game is won
  useEffect(() => {
    if (gameStatus === 'won') {
      // Create a small set of falling confetti pieces that will loop
      const fallingPieces = Array.from({ length: 15 }, generateConfettiPiece);
      setFallingConfetti(fallingPieces);
      setLandedConfetti([]);

      // Add pieces to the landed pile every 150ms to match fall rate (~7-8 pieces per second)
      const landingInterval = setInterval(() => {
        setLandedConfetti(prev => {
          // Calculate current pile height
          const pileHeight = Math.floor(prev.length / 10) * 2;

          // Stop adding if pile reaches the stick figure's head level (around y=100)
          if (pileHeight >= 200) {
            return prev; // Don't add more confetti
          }

          const newLandedPiece = generateConfettiPiece();
          return [...prev, newLandedPiece];
        });
      }, 150);

      return () => clearInterval(landingInterval);
    } else {
      setFallingConfetti([]);
      setLandedConfetti([]);
    }
  }, [gameStatus, generateConfettiPiece]);

  // Generate descriptive text for the stick figure state
  const getStickFigureDescription = () => {
    if (gameStatus === 'won') {
      return 'Game won! Celebrating stick figure with confetti falling.';
    }
    if (gameStatus === 'lost') {
      return `Game lost. Complete hangman drawing showing all ${wrongGuesses} wrong guesses.`;
    }
    const parts = [];
    if (showHead) parts.push('head');
    if (showBody) parts.push('body');
    if (showLeftArm) parts.push('left arm');
    if (showRightArm) parts.push('right arm');
    if (showLeftLeg) parts.push('left leg');
    if (showRightLeg) parts.push('right leg');

    if (parts.length === 0) {
      return `Hangman drawing: Gallows only. ${wrongGuesses} wrong guesses out of 6.`;
    }
    return `Hangman drawing showing: ${parts.join(', ')}. ${wrongGuesses} wrong guesses out of 6.`;
  };

  return (
    <StickFigureContainer>
      <Box sx={{ position: 'relative', width: '200px', height: '200px' }}>
        {/* Gallows - only show when game is not won */}
        <svg
          width="200"
          height="200"
          viewBox="0 0 200 200"
          role="img"
          aria-label={getStickFigureDescription()}
        >
          {gameStatus !== 'won' && (
            <>
              {/* Base */}
              <line
                x1="20"
                y1="180"
                x2="180"
                y2="180"
                stroke="#8B4513"
                strokeWidth="4"
              />
              {/* Vertical pole */}
              <line
                x1="40"
                y1="180"
                x2="40"
                y2="40"
                stroke="#8B4513"
                strokeWidth="4"
              />
              {/* Top horizontal */}
              <line
                x1="38"
                y1="40"
                x2="141"
                y2="40"
                stroke="#8B4513"
                strokeWidth="4"
              />
              {/* Rope */}
              <line
                x1="140"
                y1="40"
                x2="140"
                y2="60"
                stroke="#8B4513"
                strokeWidth="2"
              />
            </>
          )}

          {/* Hanging Stick Figure - only show when game is not won */}
          {gameStatus !== 'won' && (
            <>
              {/* Stick Figure - Head */}
              {showHead && (
                <circle
                  cx="140"
                  cy="70"
                  r="10"
                  fill="none"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}

              {/* Stick Figure - Body */}
              {showBody && (
                <line
                  x1="140"
                  y1="80"
                  x2="140"
                  y2="120"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}

              {/* Stick Figure - Left Arm */}
              {showLeftArm && (
                <line
                  x1="140"
                  y1="90"
                  x2="120"
                  y2="110"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}

              {/* Stick Figure - Right Arm */}
              {showRightArm && (
                <line
                  x1="140"
                  y1="90"
                  x2="160"
                  y2="110"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}

              {/* Stick Figure - Left Leg */}
              {showLeftLeg && (
                <line
                  x1="140"
                  y1="120"
                  x2="120"
                  y2="150"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}

              {/* Stick Figure - Right Leg */}
              {showRightLeg && (
                <line
                  x1="140"
                  y1="120"
                  x2="160"
                  y2="150"
                  stroke="#344f6b"
                  strokeWidth="3"
                />
              )}
            </>
          )}

          {/* Complete happy stick figure celebrating on the ground when game is won */}
          {gameStatus === 'won' && (
            <>
              {/* Ground line */}
              <line
                x1="20"
                y1="180"
                x2="180"
                y2="180"
                stroke="#8B4513"
                strokeWidth="4"
              />

              {/* Head */}
              <circle
                cx="100"
                cy="110"
                r="10"
                fill="none"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="cy"
                  values="110;105;110"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
              {/* Body */}
              <line
                x1="100"
                y1="120"
                x2="100"
                y2="160"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="y1"
                  values="120;115;120"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="y2"
                  values="160;155;160"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Left Arm - raised in celebration */}
              <line
                x1="100"
                y1="130"
                x2="80"
                y2="100"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="y1"
                  values="130;125;130"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="y2"
                  values="100;95;100"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="x2"
                  values="80;75;80"
                  dur="0.4s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Right Arm - raised in celebration */}
              <line
                x1="100"
                y1="130"
                x2="120"
                y2="100"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="y1"
                  values="130;125;130"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="y2"
                  values="100;95;100"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="x2"
                  values="120;125;120"
                  dur="0.4s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Left Leg */}
              <line
                x1="100"
                y1="160"
                x2="80"
                y2="180"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="y1"
                  values="160;155;160"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Right Leg */}
              <line
                x1="100"
                y1="160"
                x2="120"
                y2="180"
                stroke="#344f6b"
                strokeWidth="3"
              >
                <animate
                  attributeName="y1"
                  values="160;155;160"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Happy eyes */}
              <circle cx="98" cy="108" r="1.5" fill="#344f6b">
                <animate
                  attributeName="cy"
                  values="108;103;108"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="102" cy="108" r="1.5" fill="#344f6b">
                <animate
                  attributeName="cy"
                  values="108;103;108"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
              {/* Happy mouth */}
              <path
                d="M 97 112 Q 100 115 103 112"
                fill="none"
                stroke="#344f6b"
                strokeWidth="2"
              >
                <animate
                  attributeName="d"
                  values="M 97 112 Q 100 115 103 112;M 97 107 Q 100 110 103 107;M 97 112 Q 100 115 103 112"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </path>

              {/* Confetti system */}
              <g className="confetti" aria-hidden="true">
                {/* Falling confetti - loops continuously */}
                {fallingConfetti.map(piece => {
                  // Calculate current pile height for falling pieces to land on
                  const pileHeight = Math.floor(landedConfetti.length / 15) * 1; // Match much denser stacking
                  const landingY = 180 - pileHeight;

                  return (
                    <rect
                      key={piece.id}
                      x={piece.x}
                      y={piece.y}
                      width={piece.size}
                      height={piece.size}
                      fill={piece.color}
                    >
                      <animate
                        attributeName="y"
                        values={`${piece.y};${landingY}`}
                        dur={`${piece.speed}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="x"
                        values={`${piece.x};${piece.x + piece.sway};${piece.x}`}
                        dur={`${piece.speed}s`}
                        repeatCount="indefinite"
                      />
                    </rect>
                  );
                })}

                {/* Landed confetti - static pile - decorative only */}
                {landedConfetti.map((piece, index) => {
                  const stackHeight = Math.floor(index / 15) * 1; // Much denser: every 15 pieces, only 1px height
                  const finalY = 180 - piece.size - stackHeight;
                  const finalX = piece.x + piece.finalXOffset;

                  return (
                    <rect
                      key={piece.id}
                      x={finalX}
                      y={finalY}
                      width={piece.size}
                      height={piece.size}
                      fill={piece.color}
                    />
                  );
                })}
              </g>
            </>
          )}

          {/* Sad face when game is lost */}
          {gameStatus === 'lost' && (
            <>
              {/* Sad eyes */}
              <circle cx="138" cy="66" r="1.5" fill="#344f6b" />
              <circle cx="142" cy="66" r="1.5" fill="#344f6b" />
              {/* Sad mouth */}
              <path
                d="M 137 75 Q 140 70 143 75"
                fill="none"
                stroke="#344f6b"
                strokeWidth="2"
              />
            </>
          )}
        </svg>
      </Box>
    </StickFigureContainer>
  );
};

export default StickFigure;
